import React, { lazy, Suspense, useState, useEffect } from 'react';
import { formSchema, type FormData } from '../types/form';
import {
  formatPhoneNumber,
  validateEmail,
  validatePhone,
} from '../utils/validation';
import { FormController } from '../controllers/FormController';
import { storageController } from '../controllers/StorageController';
import {
  FormRouteManager,
  FormRouteEnum,
  getFlowType,
  type FlowType,
  type FormRoute,
  useFormRouting,
} from '../routes/FormRoutes';
import { marketingParams } from '../utils/marketingParams';
import StartForm from './forms/StartForm';
import LoanPurposeForm from './forms/LoanPurposeForm';
import LoanAmountForm from './forms/LoanAmountForm';
import PropertyStatusForm from './forms/PropertyStatusForm';
import EmploymentStatusForm from './forms/EmploymentStatusForm';
import EmploymentFrequencyForm from './forms/EmploymentFrequencyForm';
import AnnualIncomeForm from './forms/AnnualIncomeForm';
import EducationForm from './forms/EducationForm';
import EmailForm from './forms/EmailForm';
import BirthDateForm from './forms/BirthDateForm';
import ContactInfoForm from './forms/ContactInfoForm';
import PhoneVerificationForm from './forms/PhoneVerificationForm';
import SSNVerificationForm from './forms/SSNVerificationForm';
import ManualVerificationForm from './forms/ManualVerificationForm';
import SuccessStep from './forms/SuccessStepModular';
import EmailVerificationModal from './EmailVerificationModal';
import ProgressBar from './ProgressBar';
import FormHeader from './FormHeader';
import LoadingOverlay from './LoadingOverlay';
import TrustpilotWidget from './TrustpilotWidget';
import ApplicationBlocked from './forms/ApplicationBlocked';
import UnsuccessfulApplication from './forms/UnsuccessfulApplication';
import { campaignVariantService } from '../services/campaignVariants';
import { formDefaultsService } from '../services/formDefaults';
import { clientConfig } from '../config/clientConfig';
import { trackFormStep } from '../utils/formTrack';

// Lazy load non-critical components
const DeveloperTools = lazy(() => import('./DeveloperTools'));
const SecurityTiles = lazy(() => import('./SecurityTiles'));
const TrustedPartners = lazy(() => import('./TrustedPartners'));

const MAX_CONTACT_ATTEMPTS = 3;
const SKIP_FREQUENCY_STATUSES = ['not_employed', 'retired', 'other'];

type ManualVerificationReason = 'sms_failure' | 'create_failure';

const ROUTES_WITHOUT_BACK = new Set<string>([
  FormRouteEnum.START,
  FormRouteEnum.VERIFY_SSN,
  FormRouteEnum.SUCCESS,
  FormRouteEnum.UNSUCCESSFUL,
  FormRouteEnum.BLOCK,
]);

export default function MicroForm() {
  const { currentPath, navigateToRoute } = useFormRouting();

  const mockDelay = 5000;
  const delay = async () => {
    await new Promise((resolve) => setTimeout(resolve, mockDelay));
  };

  // Check for BPO agent flag in URL
  const [isBpoAgent] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bpo') === 'true';
  });

  const [flow] = useState<FlowType>(() => getFlowType());

  const [formController] = useState(() => {
    const controller = new FormController();
    // Set FormController reference in StorageController
    storageController.setFormController(controller);
    return controller;
  });

  const [routeManager] = useState(() => new FormRouteManager(flow));
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = storageController.getFormData();
    const variant = campaignVariantService.getCurrentVariant();
    const defaults = formDefaultsService.getDefaults();
    
    const baseFormData: FormData = {
      email: savedData?.email || '',
      phone: savedData?.phone || '',
      birthDate: savedData?.birthDate || '',
      smsConsent: savedData?.smsConsent ?? true,
      promoSmsConsent: savedData?.promoSmsConsent || false,
      smsCode: savedData?.smsCode || '',
      ssnLast4: savedData?.ssnLast4 || '',
      loanAmount: savedData?.loanAmount || variant.defaultLoanAmount || 5000,
      loanPurpose: savedData?.loanPurpose || (variant.defaultLoanPurpose as any) || "debt_consolidation",
      propertyStatus: savedData?.propertyStatus || "rent",
      employmentFrequency: savedData?.employmentFrequency || "biweekly",
      employmentStatus: savedData?.employmentStatus || "employed_full_time",
      annualIncome: savedData?.annualIncome || 50000,
      educationLevel: savedData?.educationLevel || "associate",
      offerCode: savedData?.offerCode || variant.offerCode,
      firstName: savedData?.firstName || variant.firstName
    };
    
    // Apply form defaults
    return formDefaultsService.applyToFormData(baseFormData) as FormData;
  });

  const [validationErrors, setValidationErrors] = useState<{
    email: string | null;
    phone: string | null;
  }>({
    email: null,
    phone: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [manualVerificationReason, setManualVerificationReason] = useState<ManualVerificationReason | null>(null);
  const [isUnqualified, setIsUnqualified] = useState(false);
  const [contactResult, setContactResult] = useState<'accepted' | 'rejected' | null>(null);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [contactAttempts, setContactAttempts] = useState(0);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [contactCenterAvailability, setContactCenterAvailability] = useState<
    'none' | 'low' | 'high' | null
  >(null);

  const currentStep = routeManager.getCurrentStep(currentPath);

  useEffect(() => {
    trackFormStep(currentPath as FormRoute);
  }, [currentPath]);
  // Initialize marketing params on first load
  useEffect(() => {
    try {
      // Initialize campaign variants first
      campaignVariantService.initializeFromUrl();
      formDefaultsService.initializeFromUrl();
      marketingParams.initializeFromUrl();
    } catch (err) {
      console.error('Failed to initialize marketing params:', err);
    }
  }, []);

  useEffect(() => {
    storageController.setFormData(formData);
  }, [formData]);

  useEffect(() => {
    if (contactAttempts >= MAX_CONTACT_ATTEMPTS) {
      // Redirect to unsuccessful page after max attempts
      navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
    }
  }, [contactAttempts, navigateToRoute]);

  useEffect(() => {
    if (verificationAttempts >= MAX_CONTACT_ATTEMPTS) {
      // Redirect to unsuccessful page after max attempts
      navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
    }
  }, [verificationAttempts, navigateToRoute]);

  useEffect(() => {
    if (
      storageController.isApplicationBlocked() &&
      !routeManager.isBlockedRoute(currentPath as FormRoute)
    ) {
      navigateToRoute(FormRouteEnum.BLOCK);
    }
  }, [currentPath, navigateToRoute]);

  // Check contact center availability and track success page when reaching success step
  useEffect(() => {
    const checkAvailability = async () => {
      if (currentPath === FormRouteEnum.SUCCESS) {
        if (isBpoAgent) {
          setContactCenterAvailability('none');
          return;
        }

        try {
          const result = await formController.checkContactCenterAvailability();
          if (result.success) {
            setContactCenterAvailability(result.availability);
          } else {
            setContactCenterAvailability('none');
          }
        } catch (err) {
          console.error('Failed to check contact center availability:', err);
          setContactCenterAvailability('none');
        }
      }
    };

    checkAvailability();
  }, [currentPath, isBpoAgent]);

  // Track which success page variant is shown to customer
  const handleVariantShown = React.useCallback((variantId: string) => {
    formController.successPage(variantId).catch(() => void 0);
  }, [formController]);

  const handleApplicationSuccess = () => {
    // Store the first name from the contact response
    if (formController.getContactFirstName()) {
      storageController.setContactFirstName(
        formController.getContactFirstName()
      );
    }

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
  };

  const handleScheduleAppointment = async (
    selectedTime: Date
  ): Promise<boolean> => {
    try {
      const result = await formController.scheduleAppointment(selectedTime);
      return result.success;
    } catch (err) {
      console.error('Failed to schedule appointment:', err);
      return false;
    }
  };

  const handleInitiateCall = async () => {
    try {
      const result = await formController.connectCall();
      return {
        success: result.success,
        from: result.from,
        error: result.error,
      };
    } catch (err) {
      console.error('Failed to connect call:', err);
      return {
        success: false,
        error: 'Failed to connect call. Please try again.',
      };
    }
  };

  const handleApplicationOffers = async (offerId: string, storeUuid: string) => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationOffers();

    setLoadingMessage('Loading your offers...');

    await delay();

    const targetUrl = `${clientConfig.offersUrl}/?id=${storeUuid}`;
    //const targetUrl = `https://fiona.com/partner/symple-lending-loans/loans?results=${offerId}&step=results`;
    window.location.href = targetUrl;
  };

  const handleApplicationFailure = () => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'unsuccessful',
    });
    storageController.clearFormData();
    storageController.setApplicationFailed();
    navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
      setValidationErrors((prev) => ({
        ...prev,
        phone: validatePhone(formattedPhone),
      }));
    } else if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setValidationErrors((prev) => ({
        ...prev,
        email: validateEmail(value),
      }));
    } else if (name === 'smsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else if (name === 'promoSmsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);
  };

  const handleConnectBySms = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);

    setValidationErrors({
      email: emailError,
      phone: phoneError,
    });

    if (emailError || phoneError) {
      return;
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Creating secure connection...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount || 0,
        formData.loanPurpose || 'credit_card_refi',
        formData.employmentStatus || '',
        formData.employmentFrequency || '',
        formData.educationLevel || '',
        formData.annualIncome || 0,
        formData.propertyStatus || '',
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        storageController.setApplicationSmsCode();
        navigateToRoute(FormRouteEnum.VERIFY_PHONE);
        setContactAttempts(0);
      } else if (result.code === 500) {
        setManualVerificationReason('sms_failure');
        navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
      } else {
        throw new Error(result.error || 'Failed to create connection');
      }
    } catch (err) {
      const newAttempts = contactAttempts + 1;
      setContactAttempts(newAttempts);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Verifying SMS code...');
    setError(null);

    try {
      const verifyResult = await formController.verifyCode(
        formData.smsCode || '',
        formData
      );

      if (!verifyResult.success) {
        if (verifyResult.code === 500) {
          setManualVerificationReason('sms_failure');
          navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
          return;
        }
        throw new Error(verifyResult.error);
      }

      setLoadingMessage('Checking eligibility...');
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (createResult.status === 'ERROR' && createResult.code === 500) {
          setManualVerificationReason('create_failure');
          navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
          return;
        }

        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
        case 'accepted_tier2':
          if (flow === 'M1') {
            setContactResult('accepted');
            navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
          } else {
            handleApplicationSuccess();
          }
          break;

        case 'rejected':
          if (flow === 'R1') {
            navigateToRoute(FormRouteEnum.VERIFY_SSN);
          } else if (flow === 'N1') {
            setIsUnqualified(true);
            navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
          } else {
            setContactResult('rejected');
            navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
          }
          break;

        case 'ERROR':
          if (formController.getVerificationAttempts() >= 3) {
            navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
          } else {
            throw new Error(createResult.error || 'Verification failed');
          }
          break;
      }
    } catch (err) {
      setVerificationAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleResendCode = async () => {
    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Sending new code...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount || 0,
        formData.loanPurpose || 'credit_card_refi',
        formData.employmentStatus || '',
        formData.employmentFrequency || '',
        formData.educationLevel || '',
        formData.annualIncome || 0,
        formData.propertyStatus || '',
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        setFormData((prev) => ({ ...prev, smsCode: '' }));
      } else {
        throw new Error(result.error || 'Failed to send new code');
      }
    } catch (err) {
      const newAttempts = contactAttempts + 1;
      setContactAttempts(newAttempts);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReenterInfo = () => {
    setFormData((prev) => ({ ...prev, smsCode: '' }));
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const verifyWithExperianAndFallback = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string }> => {
    const experianResult = await formController.fallbackToExperian(data);

    if (experianResult.success && experianResult.qualified) {
      handleApplicationSuccess();
      return { success: true, status: 'accepted' };
    }

    if (!experianResult.success && experianResult.code === 500) {
      const manualResult = await formController.createManualLead(data);

      if (manualResult.success && manualResult.qualified) {
        handleApplicationSuccess();
        return { success: true, status: 'accepted' };
      }

      if (!manualResult.success && manualResult.code === 400) {
        setVerificationAttempts((prev) => prev + 1);
        throw new Error('Verification failed. Please check your details and try again.');
      }

      handleApplicationFailure();
      return { success: true, status: 'rejected' };
    }

    handleApplicationFailure();
    return { success: true, status: 'rejected' };
  };

  const handleManualVerification = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string; error?: string }> => {
    setLoading(true);
    setLoadingMessage('Verifying your information...');
    setError(null);

    const mergedData = { ...formData, ...data };
    setFormData(mergedData);

    try {
      if (manualVerificationReason === 'sms_failure') {
        const prospectResult = await formController.prospectVerify(mergedData);

        if (!prospectResult.success || !prospectResult.qualified) {
          handleApplicationFailure();
          return { success: true, status: 'rejected' };
        }
      }

      return await verifyWithExperianAndFallback(mergedData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleFormStep = (data: Partial<FormData>) => {
    if (currentStep === 0) {
      storageController.setApplicationStarted();
    }

    setFormData((prev) => {
      
      const updatedData = { ...prev, ...data };
      
      // Fix: Use UPDATED data, not stale formData
      marketingParams.updateFormData(updatedData);
      
      return updatedData;
    });

    if (data.employmentStatus) {
      if (SKIP_FREQUENCY_STATUSES.includes(data.employmentStatus)) {
        setFormData((prev) => ({ ...prev, employmentFrequency: 'monthly' }));
        navigateToRoute(FormRouteEnum.ANNUAL_INCOME);
        return;
      }
    }

    const nextRoute = routeManager.getNextRoute(currentStep);

    if (flow === 'M1' && nextRoute === FormRouteEnum.VERIFY_SSN) {
      if (contactResult === 'accepted') {
        handleApplicationSuccess();
      } else {
        navigateToRoute(FormRouteEnum.VERIFY_SSN);
      }
      return;
    }

    if (nextRoute) {
      navigateToRoute(nextRoute);
    }
  };

  const submitOffers = async (email: string) => {
    setLoading(true);
    setLoadingMessage('Submitting application...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.submitApplication({ ...formData, email }, ipAddress);

      if (result.success && result.offerId && result.storeUuid) {
        await handleApplicationOffers(result.offerId, result.storeUuid);
      } else {
        handleApplicationFailure();
      }
    } catch (err) {
      handleApplicationFailure();
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleOffersSubmit = async () => {
    if (!formData.email) {
      setShowEmailPrompt(true);
      return;
    }
    await submitOffers(formData.email);
  };

  const handleEmailVerified = async (email: string) => {
    setFormData(prev => ({ ...prev, email }));
    setShowEmailPrompt(false);
    await submitOffers(email);
  };

  const handleBack = () => {
    setError(null);
    const prevRoute = routeManager.getPreviousRoute(currentStep);
    if (prevRoute) {
      navigateToRoute(prevRoute);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {loading && <LoadingOverlay message={loadingMessage} />}

      {showEmailPrompt && (
        <EmailVerificationModal
          onConfirm={handleEmailVerified}
          loading={loading}
        />
      )}

      <ProgressBar
        currentStep={currentStep}
        totalSteps={
          flow === 'N1' && !isUnqualified
            ? routeManager.getQualifiedFlowTotalSteps()
            : routeManager.getTotalSteps()
        }
        onBack={!ROUTES_WITHOUT_BACK.has(currentPath) ? handleBack : undefined}
      />

      <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
        <FormHeader />

        <div className="p-6">
          {currentPath === FormRouteEnum.START && (
            <StartForm onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EMAIL && (
            <EmailForm
              formData={formData}
              onChange={handleChange}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.BIRTH_DATE && (
            <BirthDateForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.CONTACT_INFO && (
            <ContactInfoForm
              formData={formData}
              validationErrors={validationErrors}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleConnectBySms}
            />
          )}

          {currentPath === FormRouteEnum.VERIFY_PHONE && (
            <PhoneVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleCodeVerification}
              onResendCode={handleResendCode}
              onReenterInfo={handleReenterInfo}
              verificationAttempts={verificationAttempts}
            />
          )}

          {currentPath === FormRouteEnum.LOAN_AMOUNT && (
            <LoanAmountForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.LOAN_PURPOSE && (
            <LoanPurposeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.PROPERTY_STATUS && (
            <PropertyStatusForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_STATUS && (
            <EmploymentStatusForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_FREQUENCY && (
            <EmploymentFrequencyForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.ANNUAL_INCOME && (
            <AnnualIncomeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EDUCATION && (
            <EducationForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.VERIFY_SSN && (
            <SSNVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleOffersSubmit}
            />
          )}

          {currentPath === FormRouteEnum.SUCCESS && (
            <SuccessStep
              contactCenterAvailability={contactCenterAvailability}
              onScheduleAppointment={handleScheduleAppointment}
              onInitiateCall={handleInitiateCall}
              isBpoAgent={isBpoAgent}
              onVariantShown={handleVariantShown}
            />
          )}

          {currentPath === FormRouteEnum.UNSUCCESSFUL && (
            <UnsuccessfulApplication />
          )}

          {currentPath === FormRouteEnum.BLOCK && (
            <ApplicationBlocked onInitiateCall={handleInitiateCall} />
          )}

          {currentStep === 16 && (
            <ManualVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleManualVerification}
              onBack={() => navigateToRoute(FormRouteEnum.VERIFY_PHONE)}
              onChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
              showOfferCode={manualVerificationReason === 'sms_failure'}
            />
          )}
        </div>
      </div>

      {currentPath === FormRouteEnum.START && (
        <>
          <div className="mt-8 text-center">
            <TrustpilotWidget size="large" />
          </div>
        </>
      )}

      <Suspense fallback={null}>
        <SecurityTiles />
      </Suspense>

      <Suspense fallback={null}>
        <DeveloperTools formData={formData} />
      </Suspense>
    </div>
  );
}